import { useContext, useEffect, useState, useCallback } from 'react'
import { UserPlusIcon } from '@heroicons/react/24/outline'
import { AuthContext } from '../../../../Context'
import { AuthContextApi } from '../../../../Context/api'
import ObraInvestidoresTable from '../../../../Components/Table/ObraInvestidores'
import ModalVincularInvestidorObra from '../../../../Components/Modal/ObraInvestidores'

const PER_PAGE = 10

const InvestidoresObraTab = ({ obraId }) => {
    const { setters } = useContext(AuthContext)
    const { getObraInvestidores, postObraInvestidor, deleteObraInvestidor, fetchInvestidores } = useContext(AuthContextApi)

    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [modalOpen, setModalOpen] = useState(false)
    const [investidoresDisponiveis, setInvestidoresDisponiveis] = useState([])
    const [loadingSearch, setLoadingSearch] = useState(false)

    const buildParams = useCallback((overrides = {}) => new URLSearchParams({
        obra_id: obraId,
        page: overrides.page ?? page,
        per_page: PER_PAGE,
        sort: 'data_criacao',
        sort_dir: 'desc',
        ...overrides,
    }).toString(), [obraId, page])

    const load = useCallback(async (overrides = {}) => {
        if (!obraId) return
        setLoading(true)
        const data = await getObraInvestidores(buildParams(overrides))
        setRows(Array.isArray(data) ? data : [])
        setLoading(false)
    }, [obraId, getObraInvestidores, buildParams])

    useEffect(() => {
        setPage(0)
        load({ page: 0 })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obraId])

    useEffect(() => {
        if (obraId) load({ page })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    const loadInvestidoresRecentes = async () => {
        setLoadingSearch(true)
        const data = await fetchInvestidores(new URLSearchParams({
            page: 0,
            per_page: 5,
            sort: 'data_criacao',
            sort_dir: 'desc',
        }).toString())
        setInvestidoresDisponiveis(data)
        setLoadingSearch(false)
    }

    const searchInvestidor = async (term) => {
        setLoadingSearch(true)
        const data = await fetchInvestidores(new URLSearchParams({
            page: 0,
            per_page: 20,
            sort: 'nome',
            sort_dir: 'asc',
            filter: 'nome',
            search: term,
        }).toString())
        setInvestidoresDisponiveis(data)
        setLoadingSearch(false)
    }

    const openModal = () => {
        setModalOpen(true)
        loadInvestidoresRecentes()
    }

    const handleCreate = async (data) => {
        const result = await postObraInvestidor({ ...data, obra_id: obraId })
        if (result?.ok) {
            setModalOpen(false)
            setInvestidoresDisponiveis([])
            setPage(0)
            await load({ page: 0 })
        }
    }

    const confirmDelete = (item) => {
        setters.setModalMsg(true)
        setters.setPerigoModalMsg(true)
        setters.setMsgModalMsg('Remover vínculo do investidor?')
        setters.setDescModalMsg(`${item.nome_investidor} deixará de participar desta obra (${item.porcentagem}%).`)
        setters.setTitleButtonModalMsg('Remover')
        setters.setFunctionModalMsg(() => async () => {
            const ok = await deleteObraInvestidor(item.id)
            setters.setModalMsg(false)
            if (ok) await load()
        })
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-base font-bold text-gray-900">Investidores da Obra</h2>
                    <p className="text-sm text-gray-400">Gerencie a participação dos investidores nesta obra</p>
                </div>
                <button
                    onClick={openModal}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-sm transition-all hover:scale-105"
                >
                    <UserPlusIcon className="w-4 h-4" /> Vincular Investidor
                </button>
            </div>

            <ObraInvestidoresTable
                data={rows}
                loading={loading}
                onDelete={confirmDelete}
                page={page}
                recordsPerPage={PER_PAGE}
                previous={() => setPage((p) => Math.max(0, p - 1))}
                next={() => setPage((p) => p + 1)}
            />

            <ModalVincularInvestidorObra
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleCreate}
                onSearchInvestidor={searchInvestidor}
                investidoresDisponiveis={investidoresDisponiveis}
                loadingSearch={loadingSearch}
            />
        </div>
    )
}

export default InvestidoresObraTab
